import { db } from '@/db/client';
import { handleBackgroundError } from '@/lib/error-handler';
import { syncSession } from '@/lib/sync';
import { getUserSyncState, setUserLastDownloadedAt } from '@/lib/sync/state';
import type { SessionId, UserId } from '@/schemas';
import { fetchSessionIndex } from './download';

export interface SyncSessionIndexResult {
  downloadedSessions: SessionId[];
}

export async function syncSessionIndex(userId: UserId): Promise<SyncSessionIndexResult> {
  const { lastDownloadedAt = 0 } = await getUserSyncState(userId, 'session_index');
  const entries = await fetchSessionIndex(userId, { updatedAfter: lastDownloadedAt });

  if (entries.length === 0) {
    return { downloadedSessions: [] };
  }

  const maxTimestamp = entries.at(-1)?.updatedAt ?? lastDownloadedAt;
  if (maxTimestamp > lastDownloadedAt) {
    await setUserLastDownloadedAt(userId, 'session_index', maxTimestamp);
  }

  const existing = new Set(await db.sessionMetadata.toCollection().primaryKeys());

  const newSessions: SessionId[] = [];
  for (const entry of entries) {
    if (entry.deletedAt && existing.has(entry.sessionId)) {
      await db.sessionMetadata.update(entry.sessionId, {
        deletedAt: entry.deletedAt,
        updatedAt: entry.updatedAt,
      });
      continue;
    }

    if (!existing.has(entry.sessionId) && !entry.deletedAt) {
      newSessions.push(entry.sessionId);
      existing.add(entry.sessionId);
    }
  }

  for (const sessionId of newSessions) {
    try {
      await syncSession(sessionId);
    } catch (error) {
      handleBackgroundError(error, { operation: 'syncSessionFromIndex', sessionId }, 'warn');
    }
  }

  return { downloadedSessions: newSessions };
}
